import React, { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, Download, Eye, FileDown, Calculator, FileText } from "lucide-react";
import { toast } from "sonner";
import { today } from "@/lib/constants";

const FORMATS = [
  { value: "standard", label: "Standard PACEMIA" },
  { value: "ambulis", label: "Ambulis" },
  { value: "vega", label: "Vega" },
  { value: "sipam", label: "Sipam" },
];

export default function Export() {
  const firstOfMonth = today().slice(0, 8) + "01";
  const [start, setStart] = useState(firstOfMonth);
  const [end, setEnd] = useState(today());
  const [format, setFormat] = useState("standard");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sefiReady, setSefiReady] = useState(false);

  useEffect(() => {
    api.get("/company/settings").then((r) => {
      setSefiReady((r.data.sefi_columns || []).length > 0);
    }).catch(() => {});
  }, []);
  const formats = sefiReady ? [...FORMATS, { value: "sefi", label: "SEFI (modèle importé)" }] : FORMATS;

  const blobDownload = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const download = async () => {
    setLoading(true);
    try {
      const res = await api.get("/export/pivot", { params: { start, end, format }, responseType: "blob" });
      blobDownload(res.data, `pivot_${format}_${start}_${end}.csv`);
      toast.success("Export pivot généré");
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const doPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/export/pivot/preview", { params: { start, end, format } });
      setPreview(data);
      if (data.total === 0) toast.info("Aucune course facturable sur cette période");
    } catch (err) { toast.error(apiError(err)); }
    finally { setPreviewing(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/export/pivot/template", { params: { format }, responseType: "blob" });
      blobDownload(res.data, `modele_${format}.csv`);
      toast.success(`Modèle ${format} téléchargé`);
    } catch (err) { toast.error(apiError(err)); }
  };

  const [comptaLoading, setComptaLoading] = useState(null); // "csv" | "xlsx"
  const [comptaPreview, setComptaPreview] = useState(null);

  const downloadCompta = async (fmt) => {
    setComptaLoading(fmt);
    try {
      const res = await api.get("/export/comptable", { params: { start, end, fmt }, responseType: "blob" });
      blobDownload(res.data, `comptabilite_${start}_${end}.${fmt}`);
      toast.success(`Export comptable ${fmt.toUpperCase()} généré`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setComptaLoading(null); }
  };

  const previewCompta = async () => {
    try {
      const { data } = await api.get("/export/comptable/preview", { params: { start, end } });
      setComptaPreview(data);
      if (data.total === 0) toast.info("Aucune course facturable sur cette période");
    } catch (err) { toast.error(apiError(err)); }
  };

  const [month, setMonth] = useState(today().slice(0, 7));
  const [groupBy, setGroupBy] = useState("caisse");
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadReleve = async () => {
    setPdfLoading(true);
    try {
      const res = await api.get("/export/releve", { params: { month, group: groupBy }, responseType: "blob" });
      blobDownload(res.data, `releve_${month}_${groupBy}.pdf`);
      toast.success("Relevé mensuel PDF généré");
    } catch (err) { toast.error(apiError(err)); }
    finally { setPdfLoading(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-primary" /> Export Pivot & Facturation
        </h1>
        <p className="text-muted-foreground text-sm">Extraction des courses facturables (terminées / corrigées) au format pivot CSV.</p>
      </div>

      <Card className="p-6 rounded-md shadow-none space-y-4">
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <div><Label>Du</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" data-testid="export-start" /></div>
          <div><Label>Au</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" data-testid="export-end" /></div>
        </div>
        <div className="max-w-md">
          <Label>Profil logiciel de télétransmission</Label>
          <Select value={format} onValueChange={(v) => { setFormat(v); setPreview(null); }}>
            <SelectTrigger className="mt-1" data-testid="export-format-trigger">
              <SelectValue placeholder="Choisir un profil" />
            </SelectTrigger>
            <SelectContent>
              {formats.map((f) => (
                <SelectItem key={f.value} value={f.value} data-testid={`export-format-${f.value}`}>{f.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Réordonne les colonnes pour une réimportation directe dans le logiciel choisi.
            {!sefiReady && <> Pour SEFI : importez votre fichier de paramétrage dans <a href="/reglages" className="text-primary hover:underline">Réglages</a>.</>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={download} disabled={loading} className="gap-2" data-testid="export-download">
            <Download className="h-4 w-4" /> {loading ? "Génération…" : "Télécharger l'export pivot (CSV)"}
          </Button>
          <Button onClick={doPreview} disabled={previewing} variant="secondary" className="gap-2" data-testid="export-preview-btn">
            <Eye className="h-4 w-4" /> {previewing ? "Chargement…" : "Aperçu (5 lignes)"}
          </Button>
          <Button onClick={downloadTemplate} variant="outline" className="gap-2" data-testid="export-template-btn">
            <FileDown className="h-4 w-4" /> Télécharger le modèle
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Format standardisé (séparateur « ; ») compatible avec une réimportation dans les logiciels de télétransmission agréés CPAM
          (Ambulis, Vega, Sipam). Le « modèle » fournit les en-têtes + une ligne d'exemple à valider avec votre logiciel.
        </p>
      </Card>

      <Card className="p-6 rounded-md shadow-none space-y-4 border-primary/30" data-testid="export-compta-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" /> Export comptable en 1 clic</h2>
          <p className="text-sm text-muted-foreground">
            Toutes les données nécessaires à la facturation, sur la période choisie ci-dessus : kilomètres (dont km à vide),
            horaires réels (départ, prise en charge, dépose), fin de soin, patient (n° sécu, caisse, taux), véhicule,
            équipage, transport partagé, tarifs et montant estimé — avec ligne de TOTAUX.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => downloadCompta("xlsx")} disabled={!!comptaLoading} className="gap-2" data-testid="export-compta-xlsx">
            <FileSpreadsheet className="h-4 w-4" /> {comptaLoading === "xlsx" ? "Génération…" : "Télécharger Excel (.xlsx)"}
          </Button>
          <Button onClick={() => downloadCompta("csv")} disabled={!!comptaLoading} variant="secondary" className="gap-2" data-testid="export-compta-csv">
            <Download className="h-4 w-4" /> {comptaLoading === "csv" ? "Génération…" : "Télécharger CSV"}
          </Button>
          <Button onClick={previewCompta} variant="outline" className="gap-2" data-testid="export-compta-preview-btn">
            <Eye className="h-4 w-4" /> Aperçu
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          CSV : séparateur « ; », encodage UTF-8 (accents corrects dans Excel). Le montant est estimé à partir de vos
          tarifs société (base + €/km, majorations nuit/dimanche) — les courses sanitaires restent facturées au barème CPAM par votre logiciel.
        </p>
      </Card>

      {comptaPreview && (
        <Card className="p-4 rounded-md shadow-none overflow-x-auto" data-testid="export-compta-preview-card">
          <p className="text-sm font-medium mb-1">
            Aperçu comptable — {comptaPreview.total} course(s) facturable(s)
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            Totaux période : {comptaPreview.totals.DistanceKm} km · {comptaPreview.totals.KmAVide} km à vide · {comptaPreview.totals.MontantEstimeEUR} € estimés
          </p>
          {comptaPreview.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune ligne à afficher pour cette période.</p>
          ) : (
            <table className="text-xs border-collapse w-full">
              <thead>
                <tr>
                  {comptaPreview.columns.map((c) => (
                    <th key={c} className="border border-border px-2 py-1 text-left whitespace-nowrap bg-muted/50 font-semibold">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comptaPreview.rows.map((row) => (
                  <tr key={row.join("|")}>
                    {row.map((cell, j) => (
                      <td key={`${j}-${cell}`} className="border border-border px-2 py-1 whitespace-nowrap">{cell || "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      <Card className="p-6 rounded-md shadow-none space-y-4" data-testid="export-releve-card">
        <div>
          <h2 className="font-semibold flex items-center gap-2"><FileText className="h-4 w-4 text-primary" /> Relevé mensuel PDF</h2>
          <p className="text-sm text-muted-foreground">
            Relevé des transports du mois, regroupé par caisse d'assurance maladie ou par établissement, avec sous-totaux
            et total général — prêt à joindre à vos factures. Montants sanitaires au tarif conventionné CPAM (paramétrable dans Réglages).
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Mois</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-44" data-testid="export-releve-month" />
          </div>
          <div>
            <Label>Regroupement</Label>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="mt-1 w-52" data-testid="export-releve-group">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="caisse" data-testid="export-releve-group-caisse">Par caisse</SelectItem>
                <SelectItem value="etablissement" data-testid="export-releve-group-etab">Par établissement</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={downloadReleve} disabled={pdfLoading} className="gap-2" data-testid="export-releve-download">
            <FileDown className="h-4 w-4" /> {pdfLoading ? "Génération…" : "Télécharger le relevé PDF"}
          </Button>
        </div>
      </Card>

      {preview && (
        <Card className="p-4 rounded-md shadow-none overflow-x-auto" data-testid="export-preview-card">
          <p className="text-sm font-medium mb-3">
            Aperçu — profil <span className="text-primary">{preview.format}</span> · {preview.total} course(s) facturable(s)
          </p>
          {preview.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune ligne à afficher pour cette période.</p>
          ) : (
            <table className="text-xs border-collapse w-full" data-testid="export-preview-table">
              <thead>
                <tr>
                  {preview.columns.map((c) => (
                    <th key={c} className="border border-border px-2 py-1 text-left whitespace-nowrap bg-muted/50 font-semibold">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.join("|")}>
                    {row.map((cell, j) => (
                      <td key={`${j}-${cell}`} className="border border-border px-2 py-1 whitespace-nowrap">{cell || "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </div>
  );
}
